import { useMutation, useApolloClient, NetworkStatus, useQuery } from '@apollo/client'
import { ReactElement, useEffect, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import FormLayoutV2 from 'components/v2/FormLayout'
import FormLayout from 'components/v2/FormLayout'
import { Loader } from '@10xLabs/react-web/components/Loader'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { resolvers as loginResolvers } from 'resolvers/login'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { NewBusinessClient } from 'features/BusinessClients/components/NewBusinessClient'
import { useNavigate, useParams } from 'react-router-dom'
import EmptyState from 'components/v2/EmptyState'
import { Button } from 'components/v2/Button'
import { GET_BUSINESS_CLIENT } from 'features/BusinessClients/queries-legacy'
import { GTBusinessClient } from 'gql/types.generated'
import { INewBusinessClientFormValues, INewCompanyContactValues } from 'features/BusinessClients/types'
import {
  CHANGE_BUSINESS_CLIENT,
  CHANGE_BUSINESS_CLIENT_CONTACTS,
  CHANGE_BUSINESS_CLIENT_KREDIT_SETTINGS,
  CREATE_BUSINESS_CLIENT_CONTACTS,
  DELETE_BUSINESS_CLIENT_CONTACTS
} from 'features/BusinessClients/mutations-legacy'
import { isEmpty, omit } from 'lodash'
import { getEntitiesDifference, getDifference } from 'utils/object'
import { businessClientFormUtils, mapFormValuesToKreditSettingsMutation } from 'features/BusinessClients/utils'
import { usePushFileToS3 } from 'utils/file'
import { ASSIGN_BUSINESS_CLIENT_SIMS, REQUEST_BUSINESS_CLIENT_KREDIT_DEPOSIT } from 'features/BusinessClients/mutations'
import { integerToCents } from 'utils/number'

const messages = defineMessages({
  helmet: {
    id: 'companyClients.edit.helmet',
    defaultMessage: 'Edit Company Client | Nexbus Admin'
  }
})

const EditBusinessClientView = (): ReactElement => {
  const intl = useIntl()
  const { businessClientId } = useParams()
  const client = useApolloClient()
  const navigate = useNavigate()
  const login = useLogin()
  const { mapContactsToMutation, mapFormToEditMutation, mapResponseToForm } = businessClientFormUtils
  const pushFileToS3 = usePushFileToS3()
  const [s3Error, setS3Error] = useState<string | undefined>()
  const [s3Loading, setS3Loading] = useState<boolean>(false)
  client.addResolvers(loginResolvers)
  let content: ReactElement

  const [changeBusinessClient, { loading: loadingChangeBusinessClient, error: errorChangeBusinessClient }] =
    useMutation(CHANGE_BUSINESS_CLIENT)

  const [createContacts, { loading: loadingCreateContacts, error: errorCreateContacts }] = useMutation(
    CREATE_BUSINESS_CLIENT_CONTACTS
  )
  const [deleteContacts, { loading: loadingDeleteContacts, error: errorDeleteContacts }] = useMutation(
    DELETE_BUSINESS_CLIENT_CONTACTS
  )
  const [changeContacts, { loading: loadingChangeContacts, error: errorChangeContacts }] = useMutation(
    CHANGE_BUSINESS_CLIENT_CONTACTS
  )
  const [changeKreditSettings, { loading: loadingChangeKreditSettings, error: errorChangeKreditSettings }] =
    useMutation(CHANGE_BUSINESS_CLIENT_KREDIT_SETTINGS)
  const [assignBusinessClientSims, { error: errorAssignClientSims, loading: loadingAssignClientSims }] =
    useMutation(ASSIGN_BUSINESS_CLIENT_SIMS)
  const [
    requestBusinessClientKreditDeposit,
    { error: errorRequestBusinessClientKreditDeposit, loading: loadingRequestBusinessClientKreditDeposit }
  ] = useMutation(REQUEST_BUSINESS_CLIENT_KREDIT_DEPOSIT)

  const loading =
    loadingChangeBusinessClient ||
    loadingCreateContacts ||
    loadingDeleteContacts ||
    loadingChangeContacts ||
    s3Loading ||
    loadingChangeKreditSettings ||
    loadingAssignClientSims ||
    loadingRequestBusinessClientKreditDeposit
  const error =
    errorChangeBusinessClient ||
    errorCreateContacts ||
    errorDeleteContacts ||
    errorChangeContacts ||
    s3Error ||
    errorChangeKreditSettings ||
    errorAssignClientSims ||
    errorRequestBusinessClientKreditDeposit

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery<{
    businessClient: GTBusinessClient
  }>(GET_BUSINESS_CLIENT, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      id: businessClientId
    }
  })

  const updateContacts = async (
    newContacts: INewCompanyContactValues[],
    oldContacts: INewCompanyContactValues[]
  ): Promise<void> => {
    const {
      createdEntities: createdContacts,
      editedEntities: editedContacts,
      deletedEntities: deletedContacts,
      hasDifferences: hasContactsDifferences
    } = getEntitiesDifference<INewCompanyContactValues>(newContacts, oldContacts)
    if (hasContactsDifferences) {
      if (!isEmpty(deletedContacts)) {
        await deleteContacts({
          variables: {
            id: businessClientId,
            input: {
              contactIDs: deletedContacts.map(({ id }) => id)
            }
          }
        })
      }
      if (!isEmpty(editedContacts)) {
        await changeContacts({
          variables: {
            id: businessClientId,
            input: {
              contacts: mapContactsToMutation(editedContacts)
            }
          }
        })
      }
      if (!isEmpty(createdContacts)) {
        await createContacts({
          variables: {
            id: businessClientId,
            input: {
              contacts: mapContactsToMutation(createdContacts)
            }
          }
        })
      }
    }
  }
  const handleSubmit = async (formValues: INewBusinessClientFormValues): Promise<void> => {
    const oldValues = mapResponseToForm(queryData?.businessClient as GTBusinessClient)
    await updateContacts(formValues.clientContacts, oldValues.clientContacts)
    const omitedFields = ['clientContacts', 'logoFile', 'kreditsSettings', 'physicalSims', 'credits']
    const businessClientPayload = getDifference(omit(formValues, omitedFields), omit(oldValues, omitedFields))
    const kreditsSettingsPayload = getDifference(formValues.kreditsSettings, oldValues.kreditsSettings)
    const physicalSimsPayload = getEntitiesDifference(formValues.physicalSims, oldValues.physicalSims)
    const creditsPayload = getDifference(formValues.credits, oldValues.credits)

    if (!isEmpty(creditsPayload)) {
      try {
        const promises = formValues.credits.map((credit) => {
          return requestBusinessClientKreditDeposit({
            variables: {
              input: {
                receiptPath: credit.filePath ? credit.filePath : undefined,
                amount: integerToCents(credit.amount),
                ID: businessClientId
              }
            }
          })
        })

        await Promise.all(promises)
      } catch (error) {
        return
      }
    }
    if (!isEmpty(physicalSimsPayload.createdEntities)) {
      try {
        const promises = physicalSimsPayload.createdEntities.map((sim) => {
          return assignBusinessClientSims({
            variables: {
              input: {
                id: businessClientId,
                filePath: sim.filePath
              }
            }
          })
        })

        await Promise.all(promises)
      } catch (error) {
        return
      }
    }

    if (!isEmpty(businessClientPayload)) {
      if (businessClientPayload.logo && !isEmpty(formValues.logoFile)) {
        let path = ''
        try {
          setS3Loading(true)
          path = await pushFileToS3(formValues.logoFile as File)
          setS3Loading(false)
        } catch (error) {
          // @ts-expect-error FIXME
          setS3Error(error.message)
          setS3Loading(false)
          return
        }

        businessClientPayload.logo = {
          path,
          name: (formValues.logoFile as File).name
        }
      }
      const changeClientInput = mapFormToEditMutation(businessClientPayload)
      await changeBusinessClient({
        variables: {
          id: businessClientId,
          businessClient: {
            ...changeClientInput
          }
        }
      })
    }
    if (!isEmpty(kreditsSettingsPayload)) {
      const changeKreditInput = mapFormValuesToKreditSettingsMutation(
        formValues.kreditsSettings,
        businessClientId as string
      )
      await changeKreditSettings({
        variables: {
          input: changeKreditInput
        }
      })
    }

    navigate('/business-clients')
  }

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
    if (canWrite(FEATURES.COMPANY, login.accessToken.policies)) {
      refetch()
    }
  }, [])

  if (!canWrite(FEATURES.BUSINESS_CLIENT, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.BUSINESS_CLIENT} />
  }

  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <FormLayout.Content>
        <FormLayout.Card>
          <EmptyState.Container>
            <Loader />
          </EmptyState.Container>
        </FormLayout.Card>
      </FormLayout.Content>
    )
  } else if (queryError) {
    content = (
      <FormLayout.Content>
        <FormLayout.Card>
          <EmptyState.Container>
            <EmptyState.Description>
              <FormattedMessage
                id="businessClients.edit.error.description"
                defaultMessage="There was an error while loading your client"
              />
            </EmptyState.Description>
            <EmptyState.Action>
              <Button
                onClick={(): void => {
                  refetch()
                }}
                kind="primary"
              >
                <FormattedMessage id="businessClients.edit.error.action" defaultMessage="Reload your client" />
              </Button>
            </EmptyState.Action>
          </EmptyState.Container>
        </FormLayout.Card>
      </FormLayout.Content>
    )
  } else {
    content = (
      <FormLayout.Content>
        <Heading.H3>
          <FormattedMessage id="companyClients.edit.title" defaultMessage="Edit business client" />
        </Heading.H3>
        <NewBusinessClient
          businessClientId={businessClientId}
          isEdit
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
          businessClient={mapResponseToForm(queryData?.businessClient as GTBusinessClient)}
        />
      </FormLayout.Content>
    )
  }
  return <FormLayoutV2.Container>{content}</FormLayoutV2.Container>
}

export default withLastUrl(EditBusinessClientView)
